/**
 * Hooks de gestão das pessoas relacionadas de um Bond (vínculos factuais, ADR-129).
 *
 * Complementa o `useBondRelatedPersons` read-only de `use-bond.ts` (usado pela
 * aba Cliente para a contagem): aqui a listagem aceita `includeTerminated` e há
 * os 3 mutadores (add / update / terminate). Mutações usam optimistic-lock por
 * `version` (If-Match, ADR-071) e invalidam a chave de lista — o prefixo
 * `['bond-related-persons', bondId]` casa tanto a contagem da aba quanto a lista
 * do drawer (incl. variante com includeTerminated).
 *
 * Gate `bond-basic` (server-side em cada endpoint). 403 → toast via api-client.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  api,
  type AddRelatedPersonRequest,
  type BondRelatedPersonWireItem,
  type TerminateRelatedPersonRequest,
  type UpdateRelatedPersonRequest,
} from '../lib/api-client';

const KEY = 'bond-related-persons';

export function useManagedRelatedPersons(
  bondId: string | null,
  includeTerminated: boolean,
): {
  data: BondRelatedPersonWireItem[];
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: [KEY, bondId, { includeTerminated }],
    queryFn: async () => {
      if (bondId === null) throw new Error('bondId required');
      const res = await api.listBondRelatedPersons(bondId, { includeTerminated });
      return res.data;
    },
    enabled: bondId !== null,
    staleTime: 30_000,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

function useInvalidateRelatedPersons(bondId: string | null): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: [KEY, bondId] });
}

export function useAddRelatedPerson(
  bondId: string | null,
): ReturnType<typeof useMutation<BondRelatedPersonWireItem, Error, AddRelatedPersonRequest>> {
  const invalidate = useInvalidateRelatedPersons(bondId);
  return useMutation<BondRelatedPersonWireItem, Error, AddRelatedPersonRequest>({
    mutationFn: (body) => {
      if (bondId === null) throw new Error('bondId required');
      return api.addBondRelatedPerson(bondId, body);
    },
    onSuccess: invalidate,
  });
}

interface UpdateVars {
  relatedPersonId: string;
  version: number;
  body: UpdateRelatedPersonRequest;
}

export function useUpdateRelatedPerson(
  bondId: string | null,
): ReturnType<typeof useMutation<BondRelatedPersonWireItem, Error, UpdateVars>> {
  const invalidate = useInvalidateRelatedPersons(bondId);
  return useMutation<BondRelatedPersonWireItem, Error, UpdateVars>({
    mutationFn: ({ relatedPersonId, version, body }) => {
      if (bondId === null) throw new Error('bondId required');
      return api.updateBondRelatedPerson(bondId, relatedPersonId, version, body);
    },
    onSuccess: invalidate,
  });
}

interface TerminateVars {
  relatedPersonId: string;
  version: number;
  body: TerminateRelatedPersonRequest;
}

export function useTerminateRelatedPerson(
  bondId: string | null,
): ReturnType<typeof useMutation<BondRelatedPersonWireItem, Error, TerminateVars>> {
  const invalidate = useInvalidateRelatedPersons(bondId);
  return useMutation<BondRelatedPersonWireItem, Error, TerminateVars>({
    mutationFn: ({ relatedPersonId, version, body }) => {
      if (bondId === null) throw new Error('bondId required');
      return api.terminateBondRelatedPerson(bondId, relatedPersonId, version, body);
    },
    onSuccess: invalidate,
  });
}
